import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SUGGEST_LIST_ID, suggestOptionId } from './inlineSuggest'

/**
 * La liste de suggestions d'une saisie EN PLACE.
 *
 * Ce n'est pas une pop-in : il n'y a ni titre, ni cadre, ni bouton — rien que
 * les propositions, posées sous le mot qu'on est en train d'écrire. La saisie,
 * elle, se fait DANS l'élément du rendu (contentEditable), comme pour un titre.
 * C'est ce qui remplace la pop-in ancrée pour les types à liste : une pop-in
 * qui ne contenait qu'un champ et sa liste ajoutait un cadre, un titre, une
 * croix — et surtout SON PROPRE ascenseur par-dessus celui de la liste.
 *
 * Un seul ascenseur, donc : celui de ce panneau. Il se place sous l'élément
 * édité, bascule au-dessus faute de place, et se borne à la fenêtre.
 *
 * Patron ARIA `combobox` : c'est l'élément édité qui porte `role="combobox"`,
 * `aria-expanded` et `aria-activedescendant` (posés par l'appelant) ; ce
 * panneau porte la `listbox` et ses `option`. Le clavier reste entièrement à
 * l'élément — aucune option n'est focusable, et un `mousedown` sur une option
 * est neutralisé pour que le clic ne fasse pas perdre le focus (donc pas de
 * `blur` qui commiterait avant le choix).
 */

const MARGIN = 8
const GAP = 4

export const InlineSuggestions = ({ anchorEl, options, activeIndex, onPick, message }) => {
  const ref = useRef(null)
  const [position, setPosition] = useState(null)

  useLayoutEffect(() => {
    const place = () => {
      const panel = ref.current
      const rect = anchorEl?.isConnected ? anchorEl.getBoundingClientRect() : null
      if (!panel || !rect) return
      const { width, height } = panel.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      let top = rect.bottom + GAP
      if (top + height > vh - MARGIN) {
        const above = rect.top - GAP - height
        top = above >= MARGIN ? above : Math.max(MARGIN, vh - MARGIN - height)
      }
      const left = Math.min(Math.max(MARGIN, rect.left), Math.max(MARGIN, vw - MARGIN - width))
      setPosition({ top, left, minWidth: Math.max(rect.width, 0) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [anchorEl, options.length, message])

  // L'option retenue reste visible quand on parcourt la liste au clavier.
  useLayoutEffect(() => {
    ref.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  return createPortal(
    <div
      ref={ref}
      className="builder-inline-suggest"
      data-editor-layer=""
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        minWidth: position?.minWidth,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <ul id={SUGGEST_LIST_ID} role="listbox" className="builder-inline-suggest-list">
        {options.map((option, index) => (
          <li
            key={option.key}
            id={suggestOptionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            className="builder-inline-suggest-option"
            data-kind={option.kind}
            // Sans ceci, le clic retire le focus de l'élément édité : son
            // `blur` commiterait la saisie avant que le choix n'arrive.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(option)}
          >
            {option.label}
          </li>
        ))}
      </ul>
      {message && (
        <p
          className="builder-inline-suggest-message"
          role={message.kind === 'error' ? 'alert' : undefined}
          data-kind={message.kind}
        >
          {message.text}
        </p>
      )}
    </div>,
    document.body,
  )
}
